import { useState, useRef, useEffect, useCallback } from 'preact/hooks';
import { trackCaptureEvent } from '../lib/captureAnalytics';
import styles from './CoverFramePicker.module.css';
import sharedStyles from './ContributionShared.module.css';

interface CoverFramePickerProps {
  videoBlob: Blob;
  currentFrameTimeMs: number;
  onSelect: (result: { blob: Blob; frameTimeMs: number; selectionMode: 'manual' }) => void;
  onCancel: () => void;
}

const THUMBNAIL_COUNT = 10;
const PREVIEW_WIDTH = 600;
const PREVIEW_HEIGHT = 600;
const THUMB_SIZE = 108;

function captureFrameAtTime(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  timeMs: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error(`Seek to ${timeMs}ms timed out`));
    }, 5000);

    const cleanup = () => {
      clearTimeout(timeout);
      video.onseeked = null;
      video.onerror = null;
    };

    video.onerror = () => {
      cleanup();
      reject(new Error(`Video error during seek to ${timeMs}ms`));
    };

    video.onseeked = () => {
      cleanup();
      const ctx = canvas.getContext('2d')!;
      canvas.width = canvas.dataset.targetWidth ? parseInt(canvas.dataset.targetWidth) : video.videoWidth;
      canvas.height = canvas.dataset.targetHeight ? parseInt(canvas.dataset.targetHeight) : video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      resolve();
    };

    video.currentTime = timeMs / 1000;
  });
}

export function CoverFramePicker({
  videoBlob,
  currentFrameTimeMs,
  onSelect,
  onCancel,
}: CoverFramePickerProps) {
  const [thumbnails, setThumbnails] = useState<Array<{ dataUrl: string; timeMs: number }>>([]);
  const [selectedTimeMs, setSelectedTimeMs] = useState(currentFrameTimeMs);
  const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null);
  const [durationMs, setDurationMs] = useState(0);
  const [loading, setLoading] = useState(true);

  const videoRef = useRef<HTMLVideoElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const thumbCanvasRef = useRef<HTMLCanvasElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up blob URL and debounce timer on unmount
  useEffect(() => {
    const url = URL.createObjectURL(videoBlob);
    blobUrlRef.current = url;
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [videoBlob]);

  // Generate thumbnail strip once video metadata is loaded
  useEffect(() => {
    const video = videoRef.current;
    const thumbCanvas = thumbCanvasRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!video || !thumbCanvas || !previewCanvas || !blobUrlRef.current) return;

    video.src = blobUrlRef.current;
    video.muted = true;
    video.preload = 'auto';

    const handleLoadedMetadata = async () => {
      const dur = video.duration * 1000;
      setDurationMs(dur);

      // Generate thumbnail strip
      thumbCanvas.dataset.targetWidth = String(THUMB_SIZE);
      thumbCanvas.dataset.targetHeight = String(THUMB_SIZE);
      const frames: Array<{ dataUrl: string; timeMs: number }> = [];
      const interval = dur / THUMBNAIL_COUNT;

      for (let i = 0; i < THUMBNAIL_COUNT; i++) {
        const timeMs = Math.round(interval * i + interval / 2);
        try {
          await captureFrameAtTime(video, thumbCanvas, timeMs);
          frames.push({ dataUrl: thumbCanvas.toDataURL('image/jpeg', 0.6), timeMs });
        } catch {
          // Skip frames that fail to seek
        }
      }

      setThumbnails(frames);
      setSelectedTimeMs(currentFrameTimeMs);

      // Generate initial preview
      previewCanvas.dataset.targetWidth = String(PREVIEW_WIDTH);
      previewCanvas.dataset.targetHeight = String(PREVIEW_HEIGHT);
      await captureFrameAtTime(video, previewCanvas, currentFrameTimeMs);
      setPreviewDataUrl(previewCanvas.toDataURL('image/jpeg', 0.85));
      setLoading(false);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    return () => video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [videoBlob, currentFrameTimeMs]);

  const updatePreview = useCallback(async (timeMs: number) => {
    const video = videoRef.current;
    const canvas = previewCanvasRef.current;
    if (!video || !canvas) return;

    canvas.dataset.targetWidth = String(PREVIEW_WIDTH);
    canvas.dataset.targetHeight = String(PREVIEW_HEIGHT);
    await captureFrameAtTime(video, canvas, timeMs);
    setPreviewDataUrl(canvas.toDataURL('image/jpeg', 0.85));
  }, []);

  const handleSliderInput = useCallback((e: Event) => {
    const value = parseInt((e.target as HTMLInputElement).value);
    setSelectedTimeMs(value);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      updatePreview(value);
    }, 80);
  }, [updatePreview]);

  const handleThumbnailClick = useCallback((timeMs: number) => {
    setSelectedTimeMs(timeMs);
    updatePreview(timeMs);
  }, [updatePreview]);

  const handleConfirm = useCallback(async () => {
    const video = videoRef.current;
    const canvas = previewCanvasRef.current;
    if (!video || !canvas) return;

    canvas.dataset.targetWidth = String(PREVIEW_WIDTH);
    canvas.dataset.targetHeight = String(PREVIEW_HEIGHT);
    await captureFrameAtTime(video, canvas, selectedTimeMs);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          trackCaptureEvent('cover_user_selected', { frameTimeMs: selectedTimeMs });
          onSelect({ blob, frameTimeMs: selectedTimeMs, selectionMode: 'manual' });
        }
      },
      'image/jpeg',
      0.8,
    );
  }, [selectedTimeMs, onSelect]);

  return (
    <div class={styles.pickerContainer}>
      {/* Hidden video and canvases for frame extraction */}
      <video ref={videoRef} style={{ display: 'none' }} playsInline muted preload="auto" />
      <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
      <canvas ref={thumbCanvasRef} style={{ display: 'none' }} />

      <h3 class={sharedStyles.heading}>Choose Cover Frame</h3>

      {/* Preview image */}
      <div class={styles.previewWrap}>
        {previewDataUrl ? (
          <img src={previewDataUrl} alt="Cover frame preview" class={styles.previewImage} />
        ) : (
          <div class={styles.previewPlaceholder}>
            {loading ? 'Loading frames…' : ''}
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      <div class={styles.thumbnailStrip}>
        {thumbnails.map((thumb) => (
          <button
            key={thumb.timeMs}
            type="button"
            class={`${styles.thumbnail} ${Math.abs(thumb.timeMs - selectedTimeMs) < (durationMs / THUMBNAIL_COUNT / 2) ? styles.thumbnailSelected : ''}`}
            onClick={() => handleThumbnailClick(thumb.timeMs)}
            aria-label={`Select frame at ${(thumb.timeMs / 1000).toFixed(1)}s`}
          >
            <img src={thumb.dataUrl} alt="" width={54} height={54} />
          </button>
        ))}
      </div>

      {/* Slider scrubber */}
      <div class={styles.sliderWrap}>
        <input
          type="range"
          min={0}
          max={Math.max(durationMs, 100)}
          step={100}
          value={selectedTimeMs}
          onInput={handleSliderInput}
          class={styles.slider}
          aria-label="Scrub through video to select cover frame"
        />
        <div class={styles.sliderLabel}>
          {(selectedTimeMs / 1000).toFixed(1)}s / {(durationMs / 1000).toFixed(1)}s
        </div>
      </div>

      {/* Action buttons */}
      <div class={sharedStyles.actions}>
        <button type="button" class={sharedStyles.discardButton} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          class={sharedStyles.submitButton}
          onClick={handleConfirm}
          disabled={loading}
        >
          Use This Frame
        </button>
      </div>
    </div>
  );
}
